import React from "react";
import { ListBulletIcon, HeartIcon } from "@heroicons/react/24/outline";
import { HeartIcon as HeartIconSolid } from "@heroicons/react/24/solid";
import ListEngagement from "../../components/ListEngagement";

interface UserList {
  id: string;
  title: string;
  category: {
    id: string;
    name: string;
    icon?: string;
  };
  createdAt: string;
  views: number;
  likes: number;
  isLiked?: boolean;
  engagement?: {
    reactionCount: number;
    engagementScore: number;
    listVotes: number;
    listItemVotes: number;
    comments: number;
  };
  items: Array<{
    id: string;
    name: string;
    rank: number;
  }>;
}

interface UserListsProps {
  lists: UserList[];
  onListClick: (listId: string) => void;
  onLikeToggle: (listId: string, isLiked: boolean) => void;
  isOwnProfile: boolean;
}

const UserLists: React.FC<UserListsProps> = ({
  lists,
  onListClick,
  onLikeToggle,
  isOwnProfile,
}) => {
  console.log("UserLists received lists:", lists);
  const formatDate = (dateString: string) => {
    if (!dateString) return "Unknown";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      console.warn("Invalid date string:", dateString);
      return "Unknown";
    }

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  if (lists.length === 0) {
    return (
      <div className="card-wrap text-center">
        <ListBulletIcon className="w-12 h-12 text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-400 mb-2">No lists yet</h3>
        <p className="text-gray-500">
          {isOwnProfile
            ? "Create your first top 10 list to get started!"
            : "This user hasn't created any lists yet."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {lists.map((list) => (
        <div key={list.id} className="card-wrap transition-colors">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1 min-w-0">
              <button
                onClick={() => onListClick(list.id)}
                className="text-left w-full group"
              >
                <h3 className="text-lg font-semibold text-white group-hover:text-blue-400 transition-colors mb-1">
                  {list.title}
                </h3>
                <div className="flex items-center text-sm text-gray-400 mb-2">
                  <span className="bg-gray-700 px-2 py-1 rounded text-xs mr-2">
                    {list.category.name}
                  </span>
                  <span>{formatDate(list.createdAt)}</span>
                </div>
              </button>
            </div>

            {!isOwnProfile && (
              <button
                onClick={() => onLikeToggle(list.id, !list.isLiked)}
                className="ml-4 p-2 rounded-full hover:bg-gray-600 transition-colors"
              >
                {list.isLiked ? (
                  <HeartIconSolid className="w-5 h-5 text-red-500" />
                ) : (
                  <HeartIcon className="w-5 h-5 text-gray-400 hover:text-red-400" />
                )}
              </button>
            )}
          </div>

          {/* Top 3 items preview */}
          <div className="mb-4">
            <div className="space-y-1">
              {list.items.slice(0, 3).map((item) => (
                <div key={item.id} className="flex items-center text-sm">
                  <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-xs font-semibold mr-3">
                    {item.rank}
                  </span>
                  <span className="text-gray-300">{item.name}</span>
                </div>
              ))}
              {list.items.length > 3 && (
                <div className="text-xs text-gray-500 ml-9">
                  +{list.items.length - 3} more items
                </div>
              )}
            </div>
          </div>

          {/* Engagement Stats */}
          {list.engagement && (
            <div className="pt-2 border-t border-gray-700">
              <ListEngagement
                engagement={{
                  reactionCount: list.engagement.reactionCount || 0,
                  engagementScore: list.engagement.engagementScore || 0,
                  listVotes: list.engagement.listVotes || 0,
                  listItemVotes: list.engagement.listItemVotes || 0,
                  comments: list.engagement.comments || 0,
                }}
                showDetails={false}
              />
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default UserLists;
